import { useCallback, useEffect, useRef, useState } from "react";
import { appAudioFocus, type AppAudioLease, type CoachingAudioLease } from "@/lib/appAudioFocus";
import type { Lang } from "@/i18n/locale";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageProvider";
import { setPlaybackMode } from "@/lib/nativeAudioSession";
import type { ElevenLabsAlignment } from "@/lib/visemeMapper";
import type { MirrorSpeechObservation, MirrorSpeechObserver } from "@/lib/mirrorSpeechMetrics";
import { playOrderedSpeech, readProgressiveSpeech, ProgressiveSpeechFailure, type SpeechClip, type OrderedSpeechPlayer } from "@/lib/mirrorProgressiveSpeech";
import { playIncrementalSpeech, readIncrementalSpeech, supportsIncrementalMp3, type SpeechAudioPart, type MirrorIncrementalSpeechObserver } from "@/lib/mirrorIncrementalSpeech";
import type { MirrorProgressiveSpeechObserver } from "@/lib/mirrorProgressiveSpeechTelemetry";
import { PROGRESSIVE_SPEECH_LIMITS } from "../../supabase/functions/_shared/progressiveSpeechProtocol";
import { createSpeechDeliveryTiming, type SpeechDeliveryTiming, type SpeechDeliveryTimingReport } from "../../supabase/functions/_shared/speechDeliveryTiming";

// Alias de Lang, não uma união própria. Era "PT-BR" | "EN" | "NL" escrito à
// mão — uma segunda lista de idiomas que ninguém lembraria de atualizar. O
// eleven_flash_v2_5 é multilíngue, então não há razão para o tipo da voz ser
// mais estreito que o do app.
export type VoiceLanguage = Lang;

export interface VoiceOwnerGuard { accessToken: string; assertCurrent: () => void }
export interface SpeakOptions {
  /** Only the explicitly requested Fitness cue owns this lower-priority lease. */
  coachingAudioFocus?: CoachingAudioLease;
  ownerGuard?: VoiceOwnerGuard;
  language?: VoiceLanguage;
  voiceId?: string;
  /** Prepared Mirror playback only: best-effort, content-free observations. */
  observer?: MirrorSpeechObserver;
  /** Prepared Mirror only: negotiated speech, with a persisted opt-out. */
  progressive?: boolean;
  progressiveObserver?: MirrorProgressiveSpeechObserver;
  /** Discard incompatible whole-file/complete-clip metrics after actual v2 SSE admission. */
  incrementalAccepted?: () => void;
  /** Local observations only; no production analytics schema is repurposed. */
  incrementalObserver?: MirrorIncrementalSpeechObserver;
  /** Content-free request-clock diagnostics, separate from send/physical sound. */
  deliveryTiming?: (report: SpeechDeliveryTimingReport) => void;
}

export interface UseVoiceOptions {
  // Optional pre-mounted, gesture-unlocked HTMLAudioElement to reuse for
  // playback. Critical on iOS Safari: a `new Audio()` instance created
  // inside an async speak() callback (post-await on supabase.invoke) is
  // outside the user-gesture chain and gets blocked by autoplay policy.
  // Caller (MirrorChat) passes a JSX-rendered <audio ref={...} /> element
  // already unlocked via SILENT_MP3 inside the gesture handler. When
  // omitted (Sleep / Stories), useVoice falls back to `new Audio()`.
  externalAudioElement?: HTMLAudioElement | null;
}

interface UseVoice {
  speak: (text: string, opts?: SpeakOptions) => Promise<void>;
  stop: () => void;
  isLoading: boolean;
  isPlaying: boolean;
  error: string | null;
  // Fase 5 lip sync surface — exposed for callers that drill audio +
  // alignment to AvatarCore (currently only MirrorChat). Sleep / Stories
  // can ignore these and the hook keeps behaving as before.
  audioElement: HTMLAudioElement | null;
  alignment: ElevenLabsAlignment | null;
}

interface CachedVoice {
  audio: Blob;
  blobUrl: string;
  alignment: ElevenLabsAlignment | null;
}
interface VoiceRun {
  focusLease: AppAudioLease | null;
  epoch: number;
  controller: AbortController;
  ownerGuard?: VoiceOwnerGuard;
  observer?: MirrorSpeechObserver;
  observerEnded: boolean;
  playingObserved: boolean;
  audio: HTMLAudioElement | null;
  sourceUrl: string | null;
  phase: "tts_request" | "playback";
  player?: OrderedSpeechPlayer;
  usingSse?: boolean;
  delivery?: SpeechDeliveryTiming;
}
function ttsFailureCode(error: unknown): "tts_quota" | "tts_request" {
  try {
    if (error && typeof error === "object" && "context" in error) {
      const context = error.context;
      if (context && typeof context === "object" && "status" in context && context.status === 402) return "tts_quota";
    }
  } catch { /* Never infer quota from an exception message. */ }
  return "tts_request";
}

// Persistent server cache includes the effective voice and model revision.
// Do not memoize audio here: it bypasses delivered-audio quota and can replay
// another account\'s or an earlier selection\'s voice without asking the server.

function base64ToBlob(b64: string, mime = "audio/mpeg"): Blob {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

// Apply iOS Safari playback hardening to any audio element (external or
// internal). Idempotent — safe to call repeatedly on the same element.
function hardenForIOS(audio: HTMLAudioElement): void {
  audio.setAttribute("playsinline", "");
  audio.setAttribute("webkit-playsinline", "true");
  audio.preload = "none";
}

// Clear listeners we own on the audio element. Used on stop/end/error so
// stale handlers don't fire against a future src swap when the element is
// external and reused across speak() calls.
function clearListeners(audio: HTMLAudioElement): void {
  audio.onplay = null;
  audio.onplaying = null;
  audio.onended = null;
  audio.onerror = null;
}

// Prepared Mirror responses negotiate complete clips by default. Generic
// speech keeps its legacy contract unless explicitly opted in on this device.
// ?streaming=false persists the opt-out, including across app restarts.
const STREAMING_TTS_FLAG_KEY = "vyv_streaming_tts";

function isStreamingEnabled(preparedDefault = false): boolean {
  try {
    if (typeof window !== "undefined") {
      const param = new URLSearchParams(window.location.search).get("streaming");
      if (param === "true" || param === "false") {
        try { localStorage.setItem(STREAMING_TTS_FLAG_KEY, param); } catch { /* Honor this visit even when persistence is blocked. */ }
        return param === "true";
      }
    }
    const stored = localStorage.getItem(STREAMING_TTS_FLAG_KEY);
    return stored === "true" ? true : stored === "false" ? false : preparedDefault;
  } catch {
    // A blocked preference store cannot silently disable prepared delivery.
    return preparedDefault;
  }
}

// Split text into sentences. Final non-punctuated fragment is included so
// "marquei aqui" reads cleanly even without a trailing period.
function splitSentences(text: string): string[] {
  const matches = text.match(/[^.!?]+[.!?]+\s*|[^.!?]+$/g);
  if (!matches) return [text];
  return matches.map((s) => s.trim()).filter((s) => s.length > 0);
}

// Both whole-message and sentence playback use the stored voice, resolved on
// every request by the same server catalog as LIVE. Explicit legacy IDs are
// intentionally ignored: one selected identity must survive mode changes.
async function fetchSingleShotTTS(
  text: string,
  language: VoiceLanguage,
  _voiceId: string | undefined,
  ownerGuard?: VoiceOwnerGuard,
  observation?: {
    signal?: AbortSignal;
    requestStart: () => void;
    audioReady: (cached: boolean | null) => void;
    payloadComplete?: () => void;
    mp3Reconstructed?: () => void;
  },
): Promise<CachedVoice> {
  ownerGuard?.assertCurrent();
  observation?.requestStart();
  const { data, error: fnError } = await supabase.functions.invoke("mirror-tts", {
    ...(observation?.signal ? { signal: observation.signal } : {}),
    ...(ownerGuard ? { headers: { Authorization: "Bearer " + ownerGuard.accessToken } } : {}),
    body: { text, language, format: "base64" },
  });
  ownerGuard?.assertCurrent();
  if (fnError) throw fnError;
  observation?.payloadComplete?.();
  if (!data?.audio_base64) throw new Error("no audio_base64 returned");
  const audio = base64ToBlob(data.audio_base64, "audio/mpeg");
  observation?.mp3Reconstructed?.();
  const result = {
    audio, blobUrl: URL.createObjectURL(audio),
    alignment: null,
  };
  observation?.audioReady(typeof data.cached === "boolean" ? data.cached : null);
  return result;
}

function useVoice(options?: UseVoiceOptions): UseVoice {
  const { lang } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // State (not ref) so consumers re-render when audio + alignment change —
  // AvatarCore's useLipSync depends on the audioElement prop being stable
  // across renders to attach its RAF correctly.
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [alignment, setAlignment] = useState<ElevenLabsAlignment | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Track whether the current audio element is external (parent-owned) so
  // stop()/cleanup don't tear it down. External elements stay mounted across
  // speak() calls so the iOS-unlock context is preserved.
  const isExternalRef = useRef<boolean>(false);
  // Monotonic counter incremented on each speak()/stop(). Async sentence-chain
  // continuations check this before progressing — guarantees a stop() (or a
  // new speak()) cleanly invalidates a half-played playlist without leaking
  // state into the next playback.
  const epochRef = useRef<number>(0);
  const ownedUrls = useRef(new Set<string>());
  const speechRunRef = useRef<VoiceRun | null>(null);
  const mountedRef = useRef(true);
  const releaseAudioUrls = useCallback(() => {
    for (const url of ownedUrls.current) URL.revokeObjectURL(url);
    ownedUrls.current.clear();
  }, []);

  // Mirror the latest options into a ref so speak()'s memoized closure
  // (deps locked to [lang]) reads the current externalAudioElement at call
  // time, not from a stale capture made on first render when the JSX
  // <audio ref={...} /> hadn't attached yet.
  const optionsRef = useRef<UseVoiceOptions | undefined>(options);
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const currentRun = useCallback((run: VoiceRun): boolean => {
    if (!mountedRef.current || speechRunRef.current !== run || epochRef.current !== run.epoch) return false;
    try { run.ownerGuard?.assertCurrent(); return true; } catch {
      // Owner loss revokes locally; the origin observer must not emit under B.
      run.focusLease?.release();
      run.observerEnded = true;
      run.delivery?.discard();
      run.player?.cancel("owner_changed");
      run.controller.abort();
      if (run.audio) {
        run.audio.pause();
        clearListeners(run.audio);
      }
      epochRef.current += 1;
      speechRunRef.current = null;
      releaseAudioUrls();
      audioRef.current = null;
      isExternalRef.current = false;
      setAudioElement(null);
      setAlignment(null);
      setIsPlaying(false);
      setIsLoading(false);
      return false;
    }
  }, [releaseAudioUrls]);

  const observe = useCallback((run: VoiceRun, event: MirrorSpeechObservation) => {
    if (!currentRun(run)) return;
    if (event.stage === "playing") run.delivery?.point("html_playing");
    if (event.stage === "ended") run.delivery?.finish("complete", "ended");
    if (event.stage === "error") run.delivery?.finish("error", event.code === "tts_quota" ? "quota_rejected" : event.code === "tts_request" ? "request_failed" : "playback_failed");
    if (event.stage === "cancelled") {
      if (event.code === "owner_changed") run.delivery?.discard();
      else run.delivery?.finish("stop", event.code);
    }
    if (event.stage === "ended" || event.stage === "error" || event.stage === "cancelled") run.focusLease?.release();
    if (run.observerEnded) return;
    if (event.stage === "playing") {
      if (run.playingObserved) return;
      run.playingObserved = true;
    }
    if (event.stage === "ended" || event.stage === "error" || event.stage === "cancelled") run.observerEnded = true;
    try { void Promise.resolve(run.observer?.(event)).catch(() => {}); } catch { /* Measurement never changes speech. */ }
  }, [currentRun]);

  const currentPlayback = useCallback((run: VoiceRun, audio: HTMLAudioElement, requireCurrentSource = false) =>
    currentRun(run) && audioRef.current === audio && run.audio === audio
      && run.sourceUrl !== null && audio.src === run.sourceUrl
      && (!requireCurrentSource || audio.currentSrc === run.sourceUrl), [currentRun]);

  const stop = useCallback(() => {
    const run = speechRunRef.current;
    if (run) {
      run.player?.cancel("stop");
      observe(run, { stage: "cancelled", code: "stop" });
      run.controller.abort();
    }
    speechRunRef.current = null;
    epochRef.current += 1;
    releaseAudioUrls();
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      clearListeners(audio);
    }
    audioRef.current = null;
    isExternalRef.current = false;
    setAudioElement(null);
    setAlignment(null);
    setIsPlaying(false);
    setIsLoading(false);
  }, [releaseAudioUrls, observe]);

  const speak = useCallback(
    async (text: string, opts?: SpeakOptions) => {
      if (!mountedRef.current) return;
      opts?.ownerGuard?.assertCurrent();
      if (opts?.coachingAudioFocus && !opts.coachingAudioFocus.isCurrent()) throw new Error("audio_focus_lost");
      // `lang` DIRETO. Era `lang === "PT-BR" ? "PT-BR" : lang === "NL" ? "NL"
      // : "EN"` — um ternário que reafirmava dois valores e mandava todo o
      // resto para "EN". Quando o tipo era a união de três escrita à mão isso
      // era identidade; desde que VoiceLanguage virou `Lang` (seis idiomas) o
      // `else` passou a significar "ES, PL e BG falam inglês no mirror-tts",
      // e o compilador não tinha o que reprovar: a saída continua sendo um
      // VoiceLanguage válido. Sem ternário não há terceira perna para errar.
      const language: VoiceLanguage = opts?.language || lang;
      const voiceId = opts?.voiceId;
      const previousRun = speechRunRef.current;
      if (previousRun) {
        previousRun.player?.cancel("superseded");
        observe(previousRun, { stage: "cancelled", code: "superseded" });
        previousRun.controller.abort();
      }
      releaseAudioUrls();

      // Stop any in-flight playback first. If previous element was external,
      // we only pause + clear listeners — caller still owns the DOM node.
      const previous = audioRef.current;
      if (previous) {
        previous.pause();
        clearListeners(previous);
      }
      audioRef.current = null;
      isExternalRef.current = false;
      const myEpoch = ++epochRef.current;
      const run: VoiceRun = { focusLease: opts?.coachingAudioFocus ?? appAudioFocus.acquire("speech"), epoch: myEpoch, controller: new AbortController(),
        ownerGuard: opts?.ownerGuard, observer: opts?.observer, observerEnded: false,
        playingObserved: false, audio: null, sourceUrl: null, phase: "tts_request" };
      speechRunRef.current = run;
      const observation = (firstRequest: boolean) => ({
        // Optional prepared-client adapter preserves legacy callers' exact
        // invoke options. Abort only stops this client request, never refunds.
        ...(opts?.observer ? { signal: run.controller.signal } : {}),
        requestStart: () => { if (firstRequest) { run.delivery?.point("request_start"); observe(run, { stage: "request_start" }); } },
        payloadComplete: () => { if (firstRequest && currentRun(run)) run.delivery?.point("payload_complete"); },
        mp3Reconstructed: () => { if (firstRequest && currentRun(run)) run.delivery?.point("first_mp3_bytes_reconstructed"); },
        audioReady: (cached: boolean | null) => { if (firstRequest) observe(run, { stage: "audio_ready", cached }); },
      });
      setError(null);
      setIsLoading(true);
      setIsPlaying(false);
      setAudioElement(null);
      setAlignment(null);

      // Explicit prepared negotiation uses one request; the old generic opt-in
      // keeps its per-sentence API with bounded current+one-ahead work.
      const streamingEnabled = isStreamingEnabled(opts?.progressive === true);
      const progressive = opts?.progressive === true && streamingEnabled;
      const sentences = !progressive && streamingEnabled ? splitSentences(text) : [text];
      const chained = sentences.length > 1;
      // Prepared Mirror only. Generic Sleep/Stories retain their contract.
      if (opts?.ownerGuard && (opts.observer || opts.progressive || opts.deliveryTiming)) {
        run.delivery = createSpeechDeliveryTiming({
          scope: "client_utterance", cohort: progressive ? "negotiated_request" : "legacy_json",
          isCurrent: () => currentRun(run),
          onResult: opts.deliveryTiming ?? ((report) => console.info("[useVoice] delivery timing", report)),
        });
      }
      try {
        if (progressive || chained) {
          if (progressive && text.length > PROGRESSIVE_SPEECH_LIMITS.textChars)
            throw new ProgressiveSpeechFailure("protocol");
          if (progressive && !opts?.ownerGuard) throw new ProgressiveSpeechFailure("protocol");
          const externalEl = optionsRef.current?.externalAudioElement ?? null;
          const audio = externalEl ?? new Audio();
          isExternalRef.current = !!externalEl;
          hardenForIOS(audio); audioRef.current = audio; run.audio = audio; setAudioElement(audio);
          const assertCurrent = () => {
            if (!currentRun(run) || run.controller.signal.aborted) throw new ProgressiveSpeechFailure("cancelled");
          };
          const progressiveObservation = (event: Parameters<MirrorProgressiveSpeechObserver>[0]) => {
            if (!currentRun(run)) return;
            if (event.stage === "playing") run.delivery?.point("html_playing");
            if (event.stage === "ended") run.delivery?.finish("complete", "ended");
            if (event.stage === "error") run.delivery?.finish("error", event.code === "quota_rejected" ? "quota_rejected" : event.code === "playback" || event.code === "audio_error" ? "playback_failed" : "request_failed");
            if (event.stage === "cancelled") {
              if (event.code === "owner_changed") run.delivery?.discard();
              else run.delivery?.finish("stop", event.code);
            }
            try { void Promise.resolve(opts?.progressiveObserver?.(event)).catch(() => {}); } catch { /* Optional telemetry. */ }
          };
          const incremental = progressive && supportsIncrementalMp3();
          const incrementalObservation: MirrorIncrementalSpeechObserver = event => {
            if (!currentRun(run)) return;
            if (event.stage === "playing") run.delivery?.point("html_playing");
            if (event.stage === "ended") run.delivery?.finish("complete", "ended");
            if (event.stage === "error") run.delivery?.finish("error", event.code === "quota_rejected" ? "quota_rejected" : event.code === "playback" || event.code === "audio_error" ? "playback_failed" : "request_failed");
            if (event.stage === "cancelled") {
              if (event.code === "owner_changed") run.delivery?.discard();
              else run.delivery?.finish("stop", event.code);
            }
            try { void Promise.resolve(opts?.incrementalObserver?.(event)).catch(() => {}); } catch { /* Optional local diagnostics. */ }
          };
          let source: AsyncIterable<SpeechClip> | AsyncIterable<SpeechAudioPart>;
          if (progressive) {
            assertCurrent(); run.delivery?.point("request_start"); observe(run, { stage: "request_start" });
            if (incremental) incrementalObservation({ stage: "admitted" });
            else progressiveObservation({ stage: "admitted" });
            console.info("[useVoice] speech transport", { protocol: incremental ? 2 : 1, reason: incremental ? "mpeg_source_buffer_supported" : "mpeg_source_buffer_unavailable" });
            const url = import.meta.env.NEXT_PUBLIC_SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL;
            const key = import.meta.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY || import.meta.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
            if (typeof url !== "string" || !url || typeof key !== "string" || !key) throw new ProgressiveSpeechFailure("protocol");
            const timer = setTimeout(() => { if (speechRunRef.current === run) run.controller.abort(); }, PROGRESSIVE_SPEECH_LIMITS.requestMs);
            let response: Response;
            try {
              assertCurrent();
              response = await fetch(url.replace(/\/$/, "") + "/functions/v1/mirror-tts", {
                method: "POST", signal: run.controller.signal,
                headers: { "Content-Type": "application/json", apikey: key, Authorization: "Bearer " + opts!.ownerGuard!.accessToken },
                body: JSON.stringify({ text, language, format: "base64", progressive_protocol: incremental ? 2 : 1 }),
              });
              try { assertCurrent(); } catch (failure) {
                // A late response can outlive a transport that ignored abort.
                // Dispose captured A only, never a newer request/player.
                void response.body?.cancel().catch(() => {}); throw failure;
              }
            } finally { clearTimeout(timer); }
            assertCurrent(); run.delivery?.point("response_headers");
            source = incremental ? readIncrementalSpeech(response, {
              deliveryPoint: stage => { if (currentRun(run)) run.delivery?.point(stage); },
              signal: run.controller.signal, assertCurrent,
              accepted: () => {
                assertCurrent(); run.usingSse = true; run.observerEnded = true;
                try { opts?.incrementalAccepted?.(); } catch { /* Metrics cannot change permission. */ }
                incrementalObservation({ stage: "accepted" });
              },
              legacy: () => incrementalObservation({ stage: "json_fallback" }),
              streamDone: () => incrementalObservation({ stage: "stream_done" }),
            }) : readProgressiveSpeech(response, {
              deliveryPoint: stage => { if (currentRun(run)) run.delivery?.point(stage); },
              signal: run.controller.signal, assertCurrent,
              accepted: () => { assertCurrent(); run.usingSse = true; run.observerEnded = true; progressiveObservation({ stage: "accepted" }); },
              legacy: () => progressiveObservation({ stage: "legacy_fallback" }),
              streamDone: () => progressiveObservation({ stage: "stream_done" }),
            });
          } else {
            const pending = new Map<number, Promise<{ value: CachedVoice } | { failure: unknown }>>();
            const schedule = (index: number) => {
              if (index >= sentences.length || pending.has(index)) return;
              assertCurrent();
              pending.set(index, fetchSingleShotTTS(sentences[index], language, voiceId, opts?.ownerGuard, observation(index === 0))
                .then(value => {
                  if (!currentRun(run) || run.controller.signal.aborted) { URL.revokeObjectURL(value.blobUrl); throw new ProgressiveSpeechFailure("cancelled"); }
                  ownedUrls.current.add(value.blobUrl); return { value };
                }).catch(failure => ({ failure })));
            };
            source = (async function* () {
              // Only these two are admitted eagerly, preserving the old two-
              // sentence contract. Further work starts on actual player demand.
              schedule(0); schedule(1);
              for (let index = 0; index < sentences.length; index++) {
                assertCurrent(); schedule(index);
                const result = await pending.get(index)!;
                assertCurrent(); pending.delete(index);
                if ("failure" in result) throw result.failure;
                if (ownedUrls.current.delete(result.value.blobUrl)) URL.revokeObjectURL(result.value.blobUrl);
                yield { seq: index, audio: result.value.audio, cached: null };
                assertCurrent();
              }
            })();
          }
          const playerOptions = {
            audio, signal: run.controller.signal, assertCurrent,
            ...(!progressive ? { maxClips: sentences.length } : {}),
            ownsAudio: () => speechRunRef.current === run && audioRef.current === audio,
            requireEndedState: () => run.usingSse === true,
            setPlaybackMode,
            sourceChanged: (url: string) => { assertCurrent(); run.sourceUrl = url; if (!incremental) run.phase = "playback"; setAlignment(null); },
            loading: (value: boolean) => { if (currentRun(run)) setIsLoading(value); },
            playing: (value: boolean) => { if (currentRun(run)) setIsPlaying(value); },
            firstReady: (cached: boolean | null) => { if (incremental) run.phase = "playback"; if (progressive && !run.usingSse) observe(run, { stage: "audio_ready", cached }); },
            firstPlaying: () => { if (!run.usingSse) observe(run, { stage: "playing" }); },
            complete: () => {
              if (!currentRun(run)) return;
              if (run.usingSse) run.focusLease?.release();
              else observe(run, { stage: "ended" });
              releaseAudioUrls(); clearListeners(audio); audioRef.current = null; isExternalRef.current = false;
              setAudioElement(null); setAlignment(null); setIsPlaying(false); setIsLoading(false);
            },
            failed: (failure: ProgressiveSpeechFailure) => {
              if (!currentRun(run)) return;
              if (run.usingSse) run.focusLease?.release();
              else observe(run, { stage: "error", code: failure.code === "quota_rejected" ? "tts_quota" : failure.code === "playback" || failure.code === "audio_error" ? "playback" : run.phase === "tts_request" ? "tts_request" : "playback" });
              run.controller.abort(); releaseAudioUrls(); clearListeners(audio); audioRef.current = null; isExternalRef.current = false;
              setAudioElement(null); setAlignment(null); setIsPlaying(false); setIsLoading(false); setError("audio playback failed");
              console.warn("[useVoice] ordered speech failed");
            },
          };
          run.player = incremental ? playIncrementalSpeech({
            ...playerOptions, source: source as AsyncIterable<SpeechAudioPart>,
            abortSource: () => run.controller.abort(),
            observation: event => { if (run.usingSse) incrementalObservation(event); },
          }) : playOrderedSpeech({
            ...playerOptions, source: source as AsyncIterable<SpeechClip>,
            ...(progressive ? { observation: event => { if (run.usingSse) progressiveObservation(event); } } : {}),
          });
          await run.player.firstStarted;
          if (!currentRun(run)) return;
          return;
        }

        // ── Legacy single-shot path (default) ──────────────────────────
        const cached = await fetchSingleShotTTS(text, language, voiceId, opts?.ownerGuard, observation(true));
        if (epochRef.current !== myEpoch) {
          URL.revokeObjectURL(cached.blobUrl);
          return;
        }
        ownedUrls.current.add(cached.blobUrl);

        // Reuse external element when caller provided one — it's been
        // unlocked inside the user-gesture chain so play() won't be blocked
        // by mobile autoplay policy. Fallback to `new Audio()` for callers
        // (Sleep / Stories) that don't drill an element.
        const externalEl = optionsRef.current?.externalAudioElement ?? null;
        const audio = externalEl ?? new Audio();
        isExternalRef.current = !!externalEl;

        hardenForIOS(audio);
        run.audio = audio;
        run.sourceUrl = cached.blobUrl;
        run.phase = "playback";
        audio.src = cached.blobUrl;
        audio.load();
        audioRef.current = audio;
        setAudioElement(audio);
        setAlignment(cached.alignment);

        audio.onplay = () => {
          if (currentPlayback(run, audio)) setIsPlaying(true);
        };
        audio.onplaying = () => {
          if (currentPlayback(run, audio, true)) observe(run, { stage: "playing" });
        };
        audio.onended = () => {
          if (currentPlayback(run, audio, !!run.observer)) {
            observe(run, { stage: "ended" });
            releaseAudioUrls();
            clearListeners(audio);
            audioRef.current = null;
            isExternalRef.current = false;
            setAudioElement(null);
            setAlignment(null);
            setIsPlaying(false);
            setIsLoading(false);
          }
        };
        audio.onerror = () => {
          if (currentPlayback(run, audio)) {
            observe(run, { stage: "error", code: "audio_error" });
            releaseAudioUrls();
            clearListeners(audio);
            audioRef.current = null;
            isExternalRef.current = false;
            setAudioElement(null);
            setAlignment(null);
            setIsPlaying(false);
            setIsLoading(false);
            setError("audio playback failed");
          }
        };

        // Ensure the native session is in full-volume playback before play() —
        // STT capture leaves it ducked. No-op off-native.
        await setPlaybackMode(); opts?.ownerGuard?.assertCurrent();
        if (!currentPlayback(run, audio)) return;
        // play() must be inside a user-gesture handler on mobile; callers
        // invoke speak() directly from a click so this resolves cleanly.
        await audio.play(); opts?.ownerGuard?.assertCurrent();
        if (!currentPlayback(run, audio)) return;
        setIsLoading(false);
      } catch (e) {
        if (!currentRun(run)) return;
        observe(run, { stage: "error", code: run.phase === "tts_request" ? ttsFailureCode(e) : "playback" });
        releaseAudioUrls();
        if (run.audio) clearListeners(run.audio);
        audioRef.current = null;
        isExternalRef.current = false;
        const msg = e instanceof Error ? e.message : String(e);
        setError(msg);
        setIsLoading(false);
        setIsPlaying(false);
        setAudioElement(null);
        setAlignment(null);
        // Intentional: NO Web Speech fallback. A silent regression to a
        // robotic OS voice would mask broken state. If this throws, we
        // want the call site to know.
        throw e instanceof Error ? e : new Error(msg);
      }
    },
    [lang, releaseAudioUrls, currentRun, currentPlayback, observe],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      const run = speechRunRef.current;
      if (run) {
        run.player?.cancel("unmount");
        observe(run, { stage: "cancelled", code: "unmount" });
        run.controller.abort();
      }
      mountedRef.current = false;
      speechRunRef.current = null;
      epochRef.current += 1;
      releaseAudioUrls();
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        clearListeners(audio);
      }
      audioRef.current = null;
      isExternalRef.current = false;
    };
  }, [releaseAudioUrls, observe]);

  return { speak, stop, isLoading, isPlaying, error, audioElement, alignment };
}

export default useVoice;
export { useVoice };
